import { isEqual } from 'lodash';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { URLParamStorage, useURLParamStorage } from './adapters';
import { JSONSerializable, notify, subscribe } from './storageListeners';
import { getStorageValueSafely, setStorageValueSafely } from './storageUtils';

// Syncs react state with URL params and session storage
export function useURLParamSessionStorageState<Value extends JSONSerializable>(
    key: string,
    defaultValue: Value
): [value: Value, setValue: (value: Value) => void] {
    const urlParamStorage: URLParamStorage = useURLParamStorage();
    return useStorageState(key, defaultValue, urlParamStorage, sessionStorage);
}

// Syncs react state with URL params
export function useURLParamStorageState<Value extends JSONSerializable>(
    key: string,
    defaultValue: Value
): [value: Value, setValue: (value: Value) => void] {
    const urlParamStorage: URLParamStorage = useURLParamStorage();
    return useStorageState(key, defaultValue, urlParamStorage);
}

// Syncs react state with local storage
export function useLocalStorageState<Value extends JSONSerializable>(
    key: string,
    defaultValue: Value
): [value: Value, setValue: (value: Value) => void] {
    return useStorageState(key, defaultValue, localStorage);
}

// Syncs react state with session storage
export function useSessionStorageState<Value extends JSONSerializable>(
    key: string,
    defaultValue: Value
): [value: Value, setValue: (value: Value) => void] {
    return useStorageState(key, defaultValue, sessionStorage);
}

/**
 * Persists React state to ONE OR MORE Storage backends (for example localStorage,
 * sessionStorage, and/or URL param storage) and keeps it synchronized across subscribers.
 *
 * Use this hook when a value must survive re-renders/navigation and be shareable via
 * a storage key in external storage.
 *
 * THIS IS NOT A GENERAL-PURPOSE GLOBAL CONTEXT/STATE HOOK; USE IT ONLY FOR DATA THAT INTENTIONALLY BELONGS IN EXTERNAL STORAGE
 * (URL params, sessionStorage, localStorage, or similar storage adapters). If you only need in-memory component state, prefer `useState`.
 * If you need a general-purpose global context/state hook, use `useContext` with a custom context provider instead.
 *
 * Notes:
 * - Reads from the first storage that already contains the key; otherwise falls back to `defaultValue`.
 * - Writes updates to all provided storages.
 * - `key` and storage instances must be stable for the lifetime of the hook.
 */
export function useStorageState<Value extends JSONSerializable>(
    key: string,
    defaultValue: Value,
    ...storages: Storage[]
): [value: Value, setValue: (value: Value) => void] {
    const initialStoragesRef = useRef(storages);
    const hasStorageInstancesChanged =
        initialStoragesRef.current.length !== storages.length ||
        initialStoragesRef.current.some((storage, index) => storage !== storages[index]);
    if (hasStorageInstancesChanged) {
        throw new Error(
            `Invalid use of useStorageState. Changing the Storage instance will result in unpredictable behavior.`
        );
    }

    const initialkeyRef = useRef(key);
    if (initialkeyRef.current !== key) {
        throw new Error(
            `Invalid use of useStorageState. Changing the storage key will result in unpredictable behavior.`
        );
    }

    // This ref is used to store a stable ref of the snapshot value. It is used to avoid infinite re-renders.
    const snapshotRef = useRef<Value>(defaultValue);
    // useSyncExternalStorage syncs the snapshot with the rest of the application storages
    const snapshot = useSyncExternalStore(
        listener => subscribe(key, listener),
        () => {
            const next = getSnapshot(key, defaultValue, ...storages);
            if (!isEqual(snapshotRef.current, next)) {
                snapshotRef.current = next;
            }
            return snapshotRef.current;
        }
    );

    // This useEffect is to initialize the storage item in each storage if it is not already set
    // It iterates over the storages in order and sets the initial value to first populated storage
    useEffect(() => {
        if (snapshot == null) return;
        for (const storage of storages) {
            const storageValue = getStorageValueSafely<Value>(storage, key);
            if (!isEqual(storageValue, snapshot)) {
                setValue(snapshot);
                break;
            }
        }
    }, []);

    // This function is to set the value in each storage and notify the listeners.
    // It will iterate each storage and populate each storage with the nextValue if it is stale
    const setValue = (nextValue: Value) => {
        let shouldNotify = false;
        for (const storage of storages) {
            const storageValue = getStorageValueSafely<Value>(storage, key);
            if (!isEqual(storageValue, nextValue)) {
                shouldNotify = true;
                setStorageValueSafely(storage, key, nextValue);
            }
        }
        if (shouldNotify) notify(key);
    };

    return [snapshot, setValue];
}

// Iterate through the storages in order and return the value from the first storage that is populated.
// If no storage is populated, return the defaultValue.
function getSnapshot<Value extends JSONSerializable>(key: string, defaultValue: Value, ...storages: Storage[]): Value {
    for (const storage of storages) {
        const storageValue = getStorageValueSafely<Value>(storage, key);
        if (storageValue != null) {
            return storageValue;
        }
    }
    return defaultValue;
}
